import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Download, BarChart3, TrendingUp, Package, Factory, IndianRupee, Sparkles, FileSpreadsheet, RefreshCw, FileDown, AlertTriangle, Users, Trash2, LineChart as LineChartIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import RecipeDriftPanel from '../components/reports/RecipeDriftPanel';
import WastagePanel from '../components/reports/WastagePanel';
import ClientProfitabilityPanel from '../components/reports/ClientProfitabilityPanel';
import PriceForecastPanel from '../components/reports/PriceForecastPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const COLORS = ['#F59E0B', '#22C55E', '#3B82F6', '#8B5CF6', '#EF4444', '#06B6D4'];

export default function Reports() {
  const [tab, setTab] = useState('sales');
  const [orders, setOrders] = useState([]);
  const [batches, setBatches] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockVal, setStockVal] = useState(null);
  const [stockValLoading, setStockValLoading] = useState(false);
  const [prodCost, setProdCost] = useState(null);
  const [prodCostLoading, setProdCostLoading] = useState(false);
  // AI Insights state
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGeneratedAt, setAiGeneratedAt] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [oRes, bRes, mRes, tRes, sRes] = await Promise.all([
          axios.get(`${API}/sales/orders`, { withCredentials: true }),
          axios.get(`${API}/production/batches`, { withCredentials: true }),
          axios.get(`${API}/inventory/raw-materials`, { withCredentials: true }),
          axios.get(`${API}/cashflow/transactions`, { withCredentials: true }),
          axios.get(`${API}/cashflow/summary`, { withCredentials: true }),
        ]);
        setOrders(oRes.data); setBatches(bRes.data); setMaterials(mRes.data); setTransactions(tRes.data); setSummary(sRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const loadStockVal = async () => {
    setStockValLoading(true);
    try {
      const res = await axios.get(`${API}/reports/stock-valuation`, { withCredentials: true });
      setStockVal(res.data);
    } catch (e) { toast.error('Failed to load stock valuation'); }
    finally { setStockValLoading(false); }
  };

  const loadProdCost = async () => {
    setProdCostLoading(true);
    try {
      const res = await axios.get(`${API}/reports/production-cost`, { withCredentials: true });
      setProdCost(res.data);
    } catch (e) { toast.error('Failed to load production cost'); }
    finally { setProdCostLoading(false); }
  };

  useEffect(() => {
    if (tab === 'stockval' && !stockVal) loadStockVal();
    if (tab === 'prodcost' && !prodCost) loadProdCost();
  }, [tab]);

  if (loading) return <div className="text-zinc-400 animate-pulse text-sm">Loading reports...</div>;

  const generateAIInsights = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/dashboard/ai-insights`, {}, { withCredentials: true });
      setAiInsights(res.data.insights);
      setAiGeneratedAt(res.data.generated_at);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate insights'); }
    finally { setAiLoading(false); }
  };

  // Sales analytics
  const dispatchedOrders = orders.filter(o => o.status === 'Dispatched');
  const totalRevenue = dispatchedOrders.reduce((s, o) => s + o.total_amount, 0);
  const totalGST = orders.reduce((s, o) => s + o.gst_amount, 0);
  const avgOrderValue = dispatchedOrders.length ? totalRevenue / dispatchedOrders.length : 0;
  const gstOrders = orders.filter(o => o.gst_enabled);
  const nonGstOrders = orders.filter(o => !o.gst_enabled);
  const gstRevenue = gstOrders.reduce((s, o) => s + (o.total_amount || 0), 0);
  const nonGstRevenue = nonGstOrders.reduce((s, o) => s + (o.total_amount || 0), 0);

  const clientData = Object.entries(orders.reduce((acc, o) => {
    acc[o.client] = (acc[o.client] || 0) + o.total_amount;
    return acc;
  }, {})).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  const productData = Object.entries(orders.filter(o => o.product_name).reduce((acc, o) => {
    acc[o.product_name] = (acc[o.product_name] || { qty: 0, revenue: 0 });
    acc[o.product_name].qty += o.quantity;
    acc[o.product_name].revenue += o.total_amount;
    return acc;
  }, {})).map(([name, data]) => ({ name, ...data })).sort((a, b) => b.revenue - a.revenue);

  // Production analytics
  const completedBatches = batches.filter(b => b.status === 'Completed');
  const totalUnitsProduced = completedBatches.reduce((s, b) => s + b.quantity, 0);

  // Financial
  const gstInput = transactions.filter(t => t.type === 'outflow').reduce((s, t) => s + (t.gst_amount || 0), 0);
  const gstOutput = transactions.filter(t => t.type === 'inflow').reduce((s, t) => s + (t.gst_amount || 0), 0);
  const gstPayable = gstOutput - gstInput;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Reports & Analytics</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Business intelligence for Tirth Industries</p>
        </div>
        <button onClick={() => window.print()} className="flex items-center gap-1.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 text-xs px-3 py-2 rounded-xl transition-colors">
          <Download size={13} /> Export
        </button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Revenue', value: fmt(totalRevenue), icon: IndianRupee, color: 'text-amber-400' },
          { label: 'GST Payable', value: fmt(gstPayable), icon: BarChart3, color: 'text-blue-400' },
          { label: 'Units Produced', value: totalUnitsProduced.toLocaleString(), icon: Factory, color: 'text-purple-400' },
          { label: 'Avg Order Value', value: fmt(avgOrderValue), icon: TrendingUp, color: 'text-green-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2"><Icon size={14} className={color} /><span className="text-zinc-400 text-xs">{label}</span></div>
            <div className={`font-mono font-bold text-xl font-heading ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1 w-fit">
        {[
          ['sales', 'Sales Report', null],
          ['production', 'Production Report', null],
          ['finance', 'Financial Summary', null],
          ['inventory', 'Inventory Report', null],
          ['stockval', 'Stock Valuation', Package],
          ['prodcost', 'Production Cost', Factory],
          ['gst', 'GST Report', FileSpreadsheet],
          ['ai', 'AI Insights', Sparkles],
          ['drift', 'Recipe Cost Drift', AlertTriangle],
          ['wastage', 'Wastage Audit', Trash2],
          ['clients', 'Client Profitability', Users],
          ['forecast', 'Price Forecast', LineChartIcon],
        ].map(([key, label, Icon]) => (
          <button key={key} data-testid={`report-tab-${key}`} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${tab === key ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
            {Icon && <Icon size={11} />}
            {label}
          </button>
        ))}
      </div>

      {tab === 'drift' && <RecipeDriftPanel />}
      {tab === 'wastage' && <WastagePanel />}
      {tab === 'clients' && <ClientProfitabilityPanel />}
      {tab === 'forecast' && <PriceForecastPanel />}

      {/* Sales Report */}
      {tab === 'sales' && (
        <div className="space-y-4">
          {/* GST vs Non-GST breakdown */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: 'Total Sales', value: fmt(gstRevenue + nonGstRevenue), sub: `${orders.length} orders`, color: 'text-amber-400', bg: 'bg-amber-500/10' },
              { label: 'GST Sales (Tirth Industries)', value: fmt(gstRevenue), sub: `${gstOrders.length} invoices`, color: 'text-green-400', bg: 'bg-green-500/10' },
              { label: 'Non-GST Sales (Tirth Enterprise)', value: fmt(nonGstRevenue), sub: `${nonGstOrders.length} invoices`, color: 'text-purple-400', bg: 'bg-purple-500/10' },
              { label: 'Total GST Collected', value: fmt(totalGST), sub: `CGST ${fmt(totalGST/2)} + SGST ${fmt(totalGST/2)}`, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            ].map(({ label, value, sub, color, bg }) => (
              <div key={label} className={`${bg} border border-zinc-800 rounded-xl p-4`} data-testid={`sales-metric-${label.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="text-zinc-400 text-xs mb-1">{label}</div>
                <div className={`font-mono font-bold text-lg ${color}`}>{value}</div>
                <div className="text-zinc-600 text-[10px] mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4">Revenue by Client</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={clientData.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="name" tick={{ fill: '#71717A', fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '11px' }} formatter={(v) => [fmt(v), 'Revenue']} />
                  <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm mb-4">Orders by Status</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={['Not Accepted', 'Accepted', 'In Production', 'Packing', 'Dispatched'].map(s => ({ name: s, value: orders.filter(o => o.status === s).length })).filter(d => d.value > 0)}
                    cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                    {COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: '10px', color: '#71717A' }} />
                  <Tooltip contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">Top Products by Revenue</h3>
              <span className="text-zinc-500 text-xs">GST Total: {fmt(totalGST)}</span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-800">{['Product', 'Units Sold', 'Revenue'].map(h => <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {productData.map((p, i) => (
                  <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="px-4 py-3 text-zinc-200 text-xs">{p.name}</td>
                    <td className="px-4 py-3 font-mono text-zinc-300 text-xs">{p.qty.toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-amber-400 text-xs font-semibold">{fmt(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Production Report */}
      {tab === 'production' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 mb-2">
            {[
              { label: 'Total Batches', value: batches.length, color: 'text-amber-400' },
              { label: 'Completion Rate', value: `${batches.length ? ((completedBatches.length / batches.length) * 100).toFixed(1) : 0}%`, color: 'text-green-400' },
              { label: 'Units Produced', value: totalUnitsProduced.toLocaleString(), color: 'text-blue-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                <div className={`font-heading font-bold text-2xl ${color}`}>{value}</div>
                <div className="text-zinc-500 text-xs mt-1">{label}</div>
              </div>
            ))}
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-800">{['Batch ID', 'Product', 'Quantity', 'Status', 'Created', 'Completed'].map(h => <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="px-4 py-3 font-mono text-amber-400 text-xs font-bold">{b.id}</td>
                    <td className="px-4 py-3 text-zinc-200 text-xs">{b.product_name}</td>
                    <td className="px-4 py-3 font-mono text-zinc-300 text-xs">{b.quantity}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${b.status === 'Completed' ? 'bg-green-900/40 text-green-300' : 'bg-amber-900/40 text-amber-300'}`}>{b.status}</span></td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{b.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{b.completed_at?.slice(0, 10) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Financial */}
      {tab === 'finance' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Inflow', value: fmt(summary?.total_inflow), color: 'text-green-400' },
              { label: 'Total Outflow', value: fmt(summary?.total_outflow), color: 'text-red-400' },
              { label: 'Net Balance', value: fmt(summary?.balance), color: summary?.balance >= 0 ? 'text-amber-400' : 'text-red-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                <div className={`font-mono font-bold text-2xl ${color}`}>{value}</div>
                <div className="text-zinc-500 text-xs mt-1">{label}</div>
              </div>
            ))}
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <h3 className="font-heading text-sm font-semibold text-zinc-50 mb-2">GST Summary</h3>
            <div className="grid grid-cols-3 gap-4 mt-3">
              {[
                { label: 'GST Output (Sales)', value: fmt(gstOutput), color: 'text-green-400' },
                { label: 'GST Input (Purchase)', value: fmt(gstInput), color: 'text-blue-400' },
                { label: 'Net GST Payable', value: fmt(gstPayable), color: gstPayable > 0 ? 'text-red-400' : 'text-green-400' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-zinc-800 rounded-xl p-4 text-center">
                  <div className={`font-mono font-semibold text-lg ${color}`}>{value}</div>
                  <div className="text-zinc-500 text-xs mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <h3 className="font-heading text-sm font-semibold text-zinc-50 mb-4">Monthly Cashflow</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={summary?.monthly_chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="month" tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#71717A', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '12px' }} formatter={(v) => [fmt(v)]} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Bar dataKey="inflow" fill="#22C55E" radius={[4, 4, 0, 0]} name="Inflow" />
                <Bar dataKey="outflow" fill="#EF4444" radius={[4, 4, 0, 0]} name="Outflow" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Inventory */}
      {tab === 'inventory' && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">Raw Materials Stock Status</h3>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-800">{['Material', 'Unit', 'Current Stock', 'Reorder Level', 'Status'].map(h => <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {materials.map(m => (
                  <tr key={m.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="px-4 py-3 text-zinc-200 text-xs">{m.name}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{m.unit}</td>
                    <td className="px-4 py-3 font-mono text-zinc-200 text-xs font-semibold">{m.current_stock}</td>
                    <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{m.reorder_level}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${m.current_stock === 0 ? 'bg-red-900/40 text-red-300 border-red-700' : m.current_stock <= m.reorder_level ? 'bg-yellow-900/40 text-yellow-300 border-yellow-700' : 'bg-green-900/40 text-green-300 border-green-700'}`}>
                        {m.current_stock === 0 ? 'Out' : m.current_stock <= m.reorder_level ? 'Low' : 'OK'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stock Valuation */}
      {tab === 'stockval' && (
        <div className="space-y-4" data-testid="stock-val-tab">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">FIFO Stock Valuation Report</h3>
              <p className="text-zinc-500 text-xs mt-0.5">Lot-wise costing for raw materials + finished goods at MRP</p>
            </div>
            <button onClick={loadStockVal} disabled={stockValLoading} className="flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-400 hover:bg-zinc-800 px-3 py-2 rounded-xl transition-colors">
              <RefreshCw size={12} className={stockValLoading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>

          {stockValLoading && <div className="text-zinc-400 text-sm animate-pulse text-center py-8">Calculating valuation...</div>}

          {stockVal && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Raw Material Value', value: fmt(stockVal.raw_total), color: 'text-blue-400' },
                  { label: 'Finished Goods Value', value: fmt(stockVal.fg_total), color: 'text-green-400' },
                  { label: 'Total Inventory Value', value: fmt(stockVal.grand_total), color: 'text-amber-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                    <div className={`font-mono font-bold text-xl ${color}`}>{value}</div>
                    <div className="text-zinc-500 text-xs mt-1">{label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-zinc-800"><h3 className="font-heading font-semibold text-zinc-50 text-sm">Raw Materials — FIFO Cost Valuation</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-zinc-800">{['Material', 'Unit', 'Total Qty', 'Total Value (FIFO)', 'Avg Cost/Unit'].map(h => <th key={h} className="text-left text-zinc-500 font-medium px-4 py-3">{h}</th>)}</tr></thead>
                    <tbody>
                      {stockVal.raw_materials.map(m => (
                        <tr key={m.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="px-4 py-3 text-zinc-200 font-medium">{m.name}</td>
                          <td className="px-4 py-3 text-zinc-500">{m.unit}</td>
                          <td className="px-4 py-3 font-mono text-zinc-300">{m.total_qty}</td>
                          <td className="px-4 py-3 font-mono text-blue-400 font-semibold">{fmt(m.total_value)}</td>
                          <td className="px-4 py-3 font-mono text-zinc-400">{m.total_qty > 0 ? fmt(m.total_value / m.total_qty) : '—'}/unit</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-zinc-800"><h3 className="font-heading font-semibold text-zinc-50 text-sm">Finished Goods — MRP Valuation</h3></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-zinc-800">{['Product', 'Unit', 'Stock', 'MRP/Unit', 'Total Value'].map(h => <th key={h} className="text-left text-zinc-500 font-medium px-4 py-3">{h}</th>)}</tr></thead>
                    <tbody>
                      {stockVal.finished_goods.map(fg => (
                        <tr key={fg.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="px-4 py-3 text-zinc-200 font-medium">{fg.name}</td>
                          <td className="px-4 py-3 text-zinc-500">{fg.unit}</td>
                          <td className="px-4 py-3 font-mono text-zinc-300">{fg.current_stock}</td>
                          <td className="px-4 py-3 font-mono text-zinc-400">{fmt(fg.price_per_unit)}</td>
                          <td className="px-4 py-3 font-mono text-green-400 font-semibold">{fmt(fg.total_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Production Cost */}
      {tab === 'prodcost' && (
        <div className="space-y-4" data-testid="prod-cost-tab">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">Production Cost Analysis</h3>
              <p className="text-zinc-500 text-xs mt-0.5">Batch-wise raw material cost vs revenue potential & gross margin</p>
            </div>
            <button onClick={loadProdCost} disabled={prodCostLoading} className="flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-400 hover:bg-zinc-800 px-3 py-2 rounded-xl transition-colors">
              <RefreshCw size={12} className={prodCostLoading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>

          {prodCostLoading && <div className="text-zinc-400 text-sm animate-pulse text-center py-8">Calculating costs...</div>}

          {prodCost && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'Total Batches', value: prodCost.rows.length, color: 'text-zinc-300', isCount: true },
                  { label: 'Total Raw Cost', value: fmt(prodCost.total_cost), color: 'text-red-400' },
                  { label: 'Revenue Potential', value: fmt(prodCost.total_revenue_potential), color: 'text-green-400' },
                  { label: 'Avg Margin', value: `${prodCost.avg_margin_pct}%`, color: 'text-amber-400', isCount: true },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
                    <div className={`font-mono font-bold text-xl ${color}`}>{value}</div>
                    <div className="text-zinc-500 text-xs mt-1">{label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-zinc-800">{['Batch', 'Product', 'Qty', 'Status', 'Raw Cost', 'Revenue', 'Margin', 'Margin%', 'Cost/Unit', 'Date'].map(h => <th key={h} className="text-left text-zinc-500 font-medium px-3 py-3">{h}</th>)}</tr></thead>
                    <tbody>
                      {prodCost.rows.map(r => (
                        <tr key={r.batch_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="px-3 py-3 font-mono text-amber-400 font-semibold text-[10px]">{r.batch_no}</td>
                          <td className="px-3 py-3 text-zinc-200">{r.product_name}</td>
                          <td className="px-3 py-3 font-mono text-zinc-300">{r.quantity}</td>
                          <td className="px-3 py-3"><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.status === 'Completed' ? 'bg-green-900/40 text-green-300' : 'bg-amber-900/40 text-amber-300'}`}>{r.status}</span></td>
                          <td className="px-3 py-3 font-mono text-red-400">{fmt(r.raw_material_cost)}</td>
                          <td className="px-3 py-3 font-mono text-green-400">{fmt(r.revenue_potential)}</td>
                          <td className="px-3 py-3 font-mono text-amber-400 font-semibold">{fmt(r.gross_margin)}</td>
                          <td className="px-3 py-3 font-mono text-zinc-300">{r.margin_pct}%</td>
                          <td className="px-3 py-3 font-mono text-zinc-400">{fmt(r.cost_per_unit)}</td>
                          <td className="px-3 py-3 text-zinc-500">{r.created_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* GST Report */}
      {tab === 'gst' && (
        <div className="space-y-4">
          {/* GSTR Export Buttons */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-semibold text-zinc-50 text-sm">GSTR Filing Export</h3>
                <p className="text-zinc-500 text-xs mt-0.5">Download GST portal-ready CSV files for monthly filing</p>
              </div>
              <span className="text-[10px] bg-amber-900/30 text-amber-400 border border-amber-700/40 px-2 py-1 rounded-lg">GST Portal Ready</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet size={15} className="text-blue-400" />
                  </div>
                  <div>
                    <div className="text-zinc-100 text-sm font-semibold">GSTR-1</div>
                    <div className="text-zinc-500 text-xs mt-0.5">Outward Supplies — B2B Invoice Register</div>
                    <div className="text-zinc-600 text-[10px] mt-1">Invoice-wise GST details (CGST/SGST/IGST) for Tirth Industries</div>
                  </div>
                </div>
                <a
                  data-testid="gstr1-export-btn"
                  href={`${API}/reports/gstr1-export`}
                  className="flex items-center justify-center gap-2 w-full py-2 rounded-xl bg-blue-900/30 hover:bg-blue-800/40 border border-blue-700/50 text-blue-300 text-xs font-semibold transition-colors"
                >
                  <FileDown size={13} /> Download GSTR-1 CSV
                </a>
              </div>
              <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet size={15} className="text-green-400" />
                  </div>
                  <div>
                    <div className="text-zinc-100 text-sm font-semibold">GSTR-3B</div>
                    <div className="text-zinc-500 text-xs mt-0.5">Monthly Summary — Output vs Input Tax</div>
                    <div className="text-zinc-600 text-[10px] mt-1">Month-wise net GST payable after ITC adjustment</div>
                  </div>
                </div>
                <a
                  data-testid="gstr3b-export-btn"
                  href={`${API}/reports/gstr3b-export`}
                  className="flex items-center justify-center gap-2 w-full py-2 rounded-xl bg-green-900/30 hover:bg-green-800/40 border border-green-700/50 text-green-300 text-xs font-semibold transition-colors"
                >
                  <FileDown size={13} /> Download GSTR-3B CSV
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'GST Output (Sales)', value: fmt(orders.filter(o => o.gst_enabled).reduce((s, o) => s + (o.gst_amount || 0), 0)), color: 'text-amber-400', sub: `${orders.filter(o => o.gst_enabled).length} GST invoices` },
              { label: 'Non-GST Revenue', value: fmt(orders.filter(o => !o.gst_enabled).reduce((s, o) => s + (o.total_amount || 0), 0)), color: 'text-purple-400', sub: `${orders.filter(o => !o.gst_enabled).length} non-GST invoices` },
              { label: 'Total CGST + SGST', value: fmt(orders.filter(o => o.gst_enabled).reduce((s, o) => s + (o.gst_amount || 0), 0)), color: 'text-green-400', sub: 'Output tax liability' },
            ].map(({ label, value, color, sub }) => (
              <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="text-zinc-500 text-xs mb-1">{label}</div>
                <div className={`font-mono font-bold text-lg ${color}`}>{value}</div>
                <div className="text-zinc-600 text-[10px] mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          {/* GST-enabled orders */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">GST Invoice Register (Tirth Industries)</h3>
              <span className="text-zinc-500 text-xs">{orders.filter(o => o.gst_enabled).length} invoices</span>
            </div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-950">
                  <tr className="border-b border-zinc-800">
                    {['Invoice No.', 'Client', 'Date', 'Taxable Amount', 'CGST', 'SGST', 'Total', 'Payment'].map(h => (
                      <th key={h} className="text-left text-zinc-600 font-medium px-4 py-2.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.filter(o => o.gst_enabled).map(o => {
                    const taxable = (o.total_amount || 0) - (o.gst_amount || 0);
                    const half = (o.gst_amount || 0) / 2;
                    return (
                      <tr key={o.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                        <td className="px-4 py-2 font-mono text-amber-400 text-[10px]">{o.invoice_number || o.id}</td>
                        <td className="px-4 py-2 text-zinc-300">{o.client}</td>
                        <td className="px-4 py-2 text-zinc-500">{o.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-2 font-mono text-zinc-300">{fmt(taxable)}</td>
                        <td className="px-4 py-2 font-mono text-blue-400">{fmt(half)}</td>
                        <td className="px-4 py-2 font-mono text-blue-400">{fmt(half)}</td>
                        <td className="px-4 py-2 font-mono font-semibold text-zinc-50">{fmt(o.total_amount)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${o.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300' : o.payment_status === 'Partial' ? 'bg-amber-900/40 text-amber-300' : 'bg-red-900/40 text-red-300'}`}>
                            {o.payment_status || 'Unpaid'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Non-GST orders */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">Non-GST Sales Register (Tirth Enterprise)</h3>
              <span className="text-zinc-500 text-xs">{orders.filter(o => !o.gst_enabled).length} invoices</span>
            </div>
            <div className="overflow-x-auto max-h-60 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-zinc-950">
                  <tr className="border-b border-zinc-800">
                    {['Invoice No.', 'Client', 'Date', 'Amount', 'Payment'].map(h => (
                      <th key={h} className="text-left text-zinc-600 font-medium px-4 py-2.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.filter(o => !o.gst_enabled).map(o => (
                    <tr key={o.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                      <td className="px-4 py-2 font-mono text-purple-400 text-[10px]">{o.invoice_number || o.id}</td>
                      <td className="px-4 py-2 text-zinc-300">{o.client}</td>
                      <td className="px-4 py-2 text-zinc-500">{o.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-2 font-mono font-semibold text-zinc-50">{fmt(o.total_amount)}</td>
                      <td className="px-4 py-2">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${o.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-300'}`}>
                          {o.payment_status || 'Unpaid'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* AI Insights */}
      {tab === 'ai' && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Sparkles size={18} className="text-amber-400" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-zinc-50 text-sm">AI Business Insights — Powered by Claude</h3>
                <p className="text-zinc-500 text-xs">Real-time analysis of your ERP data with actionable recommendations</p>
              </div>
            </div>
            <button
              data-testid="generate-ai-insights-btn"
              onClick={generateAIInsights}
              disabled={aiLoading}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-sm px-5 py-2.5 rounded-xl transition-colors"
            >
              {aiLoading ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {aiLoading ? 'Analysing business data...' : aiInsights ? 'Regenerate Insights' : 'Generate AI Insights'}
            </button>
            {aiGeneratedAt && (
              <p className="text-zinc-600 text-xs mt-2">Last generated: {new Date(aiGeneratedAt).toLocaleString('en-IN')}</p>
            )}
          </div>

          {aiInsights && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6" data-testid="ai-insights-content">
              <div className="prose prose-invert prose-sm max-w-none text-zinc-300
                prose-headings:text-amber-400 prose-headings:font-heading prose-headings:font-semibold
                prose-h2:text-base prose-h2:mt-5 prose-h2:mb-2
                prose-strong:text-zinc-100 prose-strong:font-semibold
                prose-ul:space-y-1 prose-li:text-zinc-400 prose-li:text-sm
                prose-ol:space-y-1.5">
                <ReactMarkdown>{aiInsights}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
